/**
 * DockPanel — what the editor's dock shows: zehn-aip's assistant panel, where
 * this deployment uses it (VITE_AIP_PANEL_URL), and the template's
 * Placeholders, which are docs' own. A dock with neither for this file hides
 * itself.
 *
 * The assistant's frame is mounted the first time its tab is shown and then
 * kept, only hidden behind Placeholders, at a place in the tree that never
 * changes: an iframe that is unmounted or moved reloads, and the panel would
 * boot and sign in again. A panel that draws its own header (its ready names
 * 'chrome') stands alone in an assistant-only dock: the dock drops its tab
 * bar and X, and the panel's own close button collapses the dock.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ListChecks, Sparkles, X } from 'lucide-react';
import { useLoad } from '@/shared/hooks/requests.js';
import { aipPanelUrl } from '@/shared/utils/aipPanel';
import { FILE_PLACEHOLDERS } from '../api.js';
import { AipPanelFrame } from './AipPanelFrame.jsx';
import { PlaceholdersTab } from './PlaceholdersTab.jsx';

export function DockPanel({ file, editorBridge, onDocumentChanged, onCollapse }) {
  const { t } = useTranslation('assistant');
  const aipMode = Boolean(aipPanelUrl());
  const [tab, setTab] = useState(aipMode ? 'assistant' : 'placeholders');
  // The panel said it draws its own header: title, status and a close button.
  const [panelChrome, setPanelChrome] = useState(false);
  const fileId = file?.id;

  // Template placeholders: a template opens in AUTHOR mode, a document that
  // carries placeholder rows (made "from template") in FILL mode; any other
  // file has no Placeholders tab at all. Both open on that tab once.
  const placeholdersLoad = useLoad(
    { url: FILE_PLACEHOLDERS.replace('{file_id}', String(fileId)), showError: false },
    [fileId],
  );
  const placeholderItems = Array.isArray(placeholdersLoad.response?.items) ? placeholdersLoad.response.items : [];
  const placeholderMode = placeholdersLoad.response?.fileType === 'template'
    ? 'author'
    : placeholderItems.length > 0 ? 'fill' : null;
  const placeholdersTabPickedRef = useRef(false);
  useEffect(() => {
    if (placeholderMode && !placeholdersTabPickedRef.current) {
      placeholdersTabPickedRef.current = true;
      setTab('placeholders');
    }
  }, [placeholderMode]);
  const reloadPlaceholders = useCallback(() => placeholdersLoad.request(), [placeholdersLoad.request]); // eslint-disable-line react-hooks/exhaustive-deps

  // No assistant in this deployment and no placeholders on this file.
  const empty = !aipMode && placeholdersLoad.response != null && !placeholdersLoad.loading && !placeholderMode;
  useEffect(() => {
    if (empty) onCollapse?.();
  }, [empty, onCollapse]);

  const tabs = [
    ...(aipMode ? [['assistant', t('agent.tabs.assistant'), <Sparkles key="icon" className="h-3 w-3" />]] : []),
    ...(placeholderMode ? [['placeholders', t('agent.tabs.placeholders'), <ListChecks key="icon" className="h-3 w-3" />]] : []),
  ];
  const shown = tabs.some(([key]) => key === tab) ? tab : tabs[0]?.[0];

  // Mounted the first time its tab is shown, then kept (see above).
  const [assistantMounted, setAssistantMounted] = useState(false);
  if (shown === 'assistant' && !assistantMounted) setAssistantMounted(true);

  // The Assistant alone, in a panel that draws its own header: no bar, no X.
  const panelOwnsChrome = panelChrome && tabs.length === 1 && tabs[0][0] === 'assistant';
  const onPanelCapabilities = useCallback(
    (capabilities) => setPanelChrome(Array.isArray(capabilities) && capabilities.includes('chrome')),
    [],
  );

  return (
    <div className="flex h-full w-full flex-col border-l border-border bg-card">
      {!panelOwnsChrome && (
        <div className="flex flex-none border-b border-border">
          {tabs.map(([key, label, icon]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex h-9 flex-1 items-center justify-center gap-1.5 text-xs font-medium cursor-pointer ${
                shown === key
                  ? 'border-b-2 border-brand text-foreground'
                  : 'text-muted-foreground hover:bg-muted/50'
              }`}
            >
              {icon}
              {label}
            </button>
          ))}
          {tabs.length === 0 && <div className="flex-1" />}
          {onCollapse && (
            <button
              onClick={onCollapse}
              className="flex h-9 w-9 items-center justify-center text-muted-foreground hover:bg-muted/50 cursor-pointer"
              title={t('agent.panel.hide')}
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      {/* Always rendered, right after the bar, so the frame's ancestors never
          change; `hidden` (display: none) keeps the panel loaded. */}
      <div hidden={shown !== 'assistant'} className="flex min-h-0 flex-1 flex-col">
        {assistantMounted && (
          <AipPanelFrame
            file={file}
            editorBridge={editorBridge}
            onDocumentChanged={onDocumentChanged}
            visible={shown === 'assistant'}
            closable={panelOwnsChrome && Boolean(onCollapse)}
            onClose={onCollapse}
            onCapabilities={onPanelCapabilities}
          />
        )}
      </div>

      {shown === 'placeholders' && placeholderMode ? (
        <PlaceholdersTab
          key={`${fileId}-${placeholderMode}`}
          fileId={fileId}
          mode={placeholderMode}
          items={placeholderItems}
          templateEntity={placeholdersLoad.response?.templateEntity ?? null}
          sourceRef={placeholdersLoad.response?.sourceRef ?? null}
          templateLanguage={placeholdersLoad.response?.templateLanguage ?? null}
          reload={reloadPlaceholders}
          editorBridge={editorBridge}
          onDocumentChanged={onDocumentChanged}
        />
      ) : null}
    </div>
  );
}
